/**
 * Authentication Context for Avida Personal Care Platform
 * Sprint 2: Added document management permissions
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const ROLES = {
  ADMIN: 'Admin',
  COMPLIANCE_MANAGER: 'ComplianceManager',
  SCHEDULER: 'Scheduler',
  RN_SUPERVISOR: 'RNSupervisor',
  CAREGIVER: 'Caregiver',
  BILLING: 'Billing',
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('avida_token');
      const storedUser = localStorage.getItem('avida_user');
      
      if (token && storedUser) {
        try {
          const response = await authAPI.me();
          setUser(response.data);
          localStorage.setItem('avida_user', JSON.stringify(response.data));
        } catch (err) {
          localStorage.removeItem('avida_token');
          localStorage.removeItem('avida_user');
        }
      }
      setLoading(false);
    };
    
    checkAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const response = await authAPI.login(email, password);
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('avida_token', access_token);
      localStorage.setItem('avida_user', JSON.stringify(userData));
      setUser(userData);
      
      return { success: true };
    } catch (err) {
      const message = err.response && err.response.data && err.response.data.detail 
        ? err.response.data.detail 
        : 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      // Ignore logout errors
    }
    localStorage.removeItem('avida_token');
    localStorage.removeItem('avida_user');
    setUser(null);
  }, []);

  // Role checks
  const isAdmin = user && user.role === ROLES.ADMIN;
  const isComplianceManager = user && user.role === ROLES.COMPLIANCE_MANAGER;
  const isAuthenticated = !!user;
  
  // Permission checks
  const canViewUsers = isAdmin;
  const canManageUsers = isAdmin;
  const canViewAuditLogs = isAdmin || isComplianceManager;
  const canExportAuditLogs = isAdmin;
  const canViewDiagnostics = isAdmin;
  
  // Sprint 2: Document permissions
  const canManageDocumentTypes = isAdmin || isComplianceManager;
  const canManageDocuments = isAdmin || isComplianceManager;
  const canViewDocuments = true; // All authenticated users can view
  const canUploadDocuments = isAdmin || isComplianceManager || (user && user.role === ROLES.SCHEDULER);
  
  // Sprint 3: Client permissions
  const isScheduler = user && user.role === ROLES.SCHEDULER;
  const canViewClients = isAdmin || isComplianceManager || isScheduler || 
                         (user && user.role === ROLES.RN_SUPERVISOR) || 
                         (user && user.role === ROLES.BILLING);
  const canCreateClients = isAdmin || isComplianceManager || isScheduler;
  const canUpdateClients = isAdmin || isComplianceManager || isScheduler;
  const canArchiveClients = isAdmin || isComplianceManager;

  // Sprint 4: Visit/Schedule permissions
  const isCaregiver = user && user.role === ROLES.CAREGIVER;
  const isBilling = user && user.role === ROLES.BILLING;
  const canViewVisits = true; // All authenticated users can view (caregivers filtered to own)
  const canCreateVisits = isAdmin || isComplianceManager || isScheduler;
  const canEditVisits = isAdmin || isComplianceManager || isScheduler;
  const canEditNotes = isAdmin || isComplianceManager || isScheduler; // Office only
  const canCompleteVisits = isAdmin || isComplianceManager || isScheduler; // Office only
  const canMarkBillable = isAdmin || isComplianceManager || isScheduler || isBilling;

  // Sprint 6: Personnel permissions
  const canViewPersonnel = isAdmin || isComplianceManager || isScheduler;
  const canManagePersonnel = isAdmin || isComplianceManager;
  const canManageTrainings = isAdmin || isComplianceManager;

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        login,
        logout,
        isAdmin,
        isComplianceManager,
        isScheduler,
        isCaregiver,
        isBilling,
        isAuthenticated,
        canViewUsers,
        canManageUsers,
        canViewAuditLogs,
        canExportAuditLogs,
        canViewDiagnostics,
        canManageDocumentTypes,
        canManageDocuments,
        canViewDocuments,
        canUploadDocuments,
        canViewClients,
        canCreateClients,
        canUpdateClients,
        canArchiveClients,
        canViewVisits,
        canCreateVisits,
        canEditVisits,
        canEditNotes,
        canCompleteVisits,
        canMarkBillable,
        canViewPersonnel,
        canManagePersonnel,
        canManageTrainings,
        setError,
        ROLES,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
